import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ChevronDown, ChevronRight, RefreshCw, Loader2, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const CONFIDENCE_CLS = {
  high:   "text-[#00F5D4] border-[#00F5D4]/30 bg-[#00F5D4]/10",
  medium: "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  low:    "text-[#FF007A] border-[#FF007A]/30 bg-[#FF007A]/10",
};

const ALERT_STATE_CLS = {
  GREEN:   "text-[#00C896] border-[#00C896]/30 bg-[#00C896]/10",
  AMBER:   "text-[#F59E0B] border-[#F59E0B]/30 bg-[#F59E0B]/10",
  RED:     "text-[#FF007A] border-[#FF007A]/30 bg-[#FF007A]/10",
  UNKNOWN: "text-slate-400 border-slate-600/40 bg-slate-600/10",
};

const SNOOZE_REASONS = [
  { value: "real_issue", label: "Real issue" },
  { value: "being_handled", label: "Being handled" },
  { value: "not_real_issue", label: "Not a real issue" },
];

const CURRENCY_SYMBOLS = { GBP: "£", USD: "$", EUR: "€" };
const money = (value, currency = "GBP") => {
  const sym = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${sym}${Math.round(Number(value) || 0).toLocaleString()}`;
};

const DRIVER_LABELS = {
  burn_rate: "Burn rate",
  budget_remaining: "Budget remaining",
  runway_weeks: "Runway",
  time_remaining_weeks: "Time remaining",
};

const ATTRIBUTION_LABELS = {
  cost_incurred: "Cost incurred",
  pace_change: "Pace change",
  scope_fee: "Scope / fee change",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const fmtDriverValue = (driver) => {
  if (driver.value == null) return "—";
  if (driver.key === "runway_weeks" || driver.key === "time_remaining_weeks") {
    return `${driver.value} ${driver.unit || "weeks"}`;
  }
  if (driver.unit && driver.unit.includes("/week")) {
    return `${money(driver.value)}/week`;
  }
  return money(driver.value);
};

// ── margin bar — same 0-50% scale + hatch convention as Burndown.jsx's
// MarginBar, kept self-contained here so this panel doesn't couple to the
// legacy client-computed EAC drill-down. ────────────────────────────────────
const ForecastMarginBar = ({ projected, target }) => {
  const scale = 50;
  const projPct = Math.min(100, Math.max(0, (projected / scale) * 100));
  const targetPct = Math.min(100, Math.max(0, (target / scale) * 100));
  const isOnTrack = projected >= target - 5;
  const isCritical = projected < target - 15;
  const barColor = isCritical ? "#FF007A" : isOnTrack ? "#00C896" : "#F59E0B";
  const hatchWidth = Math.max(0, targetPct - projPct);
  return (
    <div className="relative">
      <div className="h-8 bg-[#1F2937] w-full relative overflow-hidden">
        <div className="absolute left-0 top-0 h-full" style={{ width: `${projPct}%`, backgroundColor: barColor }} />
        {hatchWidth > 0 && (
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${projPct}%`,
              width: `${hatchWidth}%`,
              backgroundImage: `repeating-linear-gradient(45deg, ${barColor}60 0, ${barColor}60 3px, transparent 0, transparent 50%)`,
              backgroundSize: "8px 8px",
            }}
          />
        )}
        <div className="absolute top-0 h-full w-0.5 bg-white/40" style={{ left: `${targetPct}%` }} />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-[#6B7280] mt-1">
        <span>0%</span>
        <span>target {target}%</span>
        <span>50%</span>
      </div>
    </div>
  );
};

/**
 * ForecastExplainPanel — Phase 1 spec, Unit A, build step 2: the margin bar's
 * headline, a "Why?" expander (driver table, 28-day EAC sparkline, change
 * attribution) and a "data as of" freshness footer. Reads the canonical
 * explanation payload from routes/forecast_snapshots.py (services/eac_engine.py) —
 * deterministic, no LLM output, same design rule as the rest of the engine.
 */
const ForecastExplainPanel = ({ projectId, targetMarginPct = 0, canCompute = true }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [alertState, setAlertState] = useState(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeReason, setSnoozeReason] = useState("being_handled");
  const [snoozeBusy, setSnoozeBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [latest, hist, alerts] = await Promise.all([
        api.get(`/projects/${projectId}/forecast-snapshot/latest`).catch((e) => (
          e.response?.status === 404 ? { data: null } : Promise.reject(e)
        )),
        api.get(`/projects/${projectId}/forecast-snapshots?limit=90`).catch(() => ({ data: { snapshots: [] } })),
        api.get(`/projects/${projectId}/alert-state`).catch(() => ({ data: null })),
      ]);
      setSnapshot(latest.data);
      setHistory(hist.data?.snapshots || []);
      setAlertState(alerts.data?.state || null);
    } catch (e) {
      toast.error("Couldn't load the forecast explanation");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const snooze = async (mode) => {
    setSnoozeBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/alert-state/snooze`, { mode, reason: snoozeReason });
      setAlertState((prev) => ({ ...prev, ...data }));
      setSnoozeOpen(false);
      toast.success(mode === "timed" ? "Snoozed for 7 days" : "Snoozed until it gets worse");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't snooze alerts");
    } finally {
      setSnoozeBusy(false);
    }
  };

  const unsnooze = async () => {
    setSnoozeBusy(true);
    try {
      await api.post(`/projects/${projectId}/alert-state/unsnooze`);
      setAlertState((prev) => ({ ...prev, snooze_mode: null, snoozed_until: null, snoozed_at_state: null }));
      toast.success("Snooze removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove snooze");
    } finally {
      setSnoozeBusy(false);
    }
  };

  const computeSnapshot = async () => {
    setComputing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/forecast-snapshot`);
      setSnapshot(data);
      setHistory((prev) => [...prev, data]);
      // The Unit B state machine runs server-side as part of computing the
      // snapshot — refresh the alert state so a new GREEN/AMBER/RED shows up.
      api.get(`/projects/${projectId}/alert-state`).then(({ data: a }) => setAlertState(a?.state || null)).catch(() => {});
      toast.success("Forecast snapshot computed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't compute a forecast — set a contract value first");
    } finally {
      setComputing(false);
    }
  };

  // 28-day EAC sparkline — the spec's window for the "Why?" panel.
  const sparkline = useMemo(() => {
    const cutoff = Date.now() - 28 * 24 * 60 * 60 * 1000;
    return history
      .filter((s) => s.as_of && new Date(s.as_of).getTime() >= cutoff)
      .map((s) => ({ as_of: s.as_of, eac: s.eac, label: new Date(s.as_of).toLocaleDateString(undefined, { month: "short", day: "numeric" }) }));
  }, [history]);

  if (loading) {
    return (
      <div className="border border-[#1F2937] bg-[#111827] p-5 text-center text-xs text-slate-500 font-mono" data-testid="forecast-explain-loading">
        Loading forecast…
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="border border-dashed border-[#1F2937] bg-[#111827] p-6 text-center" data-testid="forecast-explain-empty">
        <HelpCircle className="w-5 h-5 text-slate-700 mx-auto mb-2" />
        <p className="text-xs text-slate-500 font-mono mb-3">
          No forecast snapshot yet. Compute one to see the deterministic headline, drivers and attribution.
        </p>
        {canCompute && (
          <button
            onClick={computeSnapshot}
            disabled={computing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold bg-[#00F5D4]/10 hover:bg-[#00F5D4]/20 border border-[#00F5D4]/30 text-[#00F5D4] disabled:opacity-50 transition-colors"
            data-testid="compute-forecast-snapshot-btn"
          >
            {computing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Computing…</> : <>Compute forecast</>}
          </button>
        )}
      </div>
    );
  }

  const confCls = CONFIDENCE_CLS[snapshot.confidence?.level] || CONFIDENCE_CLS.medium;
  const marginPct = snapshot.forecast_margin_pct ?? 0;
  const targetPct = targetMarginPct || 0;

  return (
    <div className="border border-[#1F2937] rounded-md overflow-hidden" data-testid="forecast-explain-panel">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[#1F2937] flex items-center gap-2 bg-[#111827] flex-wrap">
        <div className="font-display font-bold tracking-tight text-sm text-white">Explainable Forecast</div>
        <span className={`text-[9px] px-2 py-1 rounded border font-mono font-bold uppercase shrink-0 ${confCls}`} data-testid="forecast-confidence-badge">
          {snapshot.confidence?.level || "—"} confidence
        </span>
        {alertState && (
          <span
            className={`text-[9px] px-2 py-1 rounded border font-mono font-bold uppercase shrink-0 ${ALERT_STATE_CLS[alertState.state] || ALERT_STATE_CLS.UNKNOWN}`}
            data-testid="alert-state-badge"
          >
            {alertState.state}
          </span>
        )}
        {alertState?.snooze_mode && (
          <span className="text-[9px] px-2 py-1 rounded border border-slate-600/40 bg-slate-600/10 text-slate-400 font-mono uppercase shrink-0" data-testid="alert-snoozed-badge">
            Snoozed{alertState.snooze_mode === "timed" ? ` until ${fmtDate(alertState.snoozed_until)}` : " until worse"}
          </span>
        )}
        {alertState && ["AMBER", "RED"].includes(alertState.state) && canCompute && (
          <div className="relative">
            {alertState.snooze_mode ? (
              <button
                onClick={unsnooze}
                disabled={snoozeBusy}
                className="text-xs font-mono text-slate-400 hover:text-white transition-colors disabled:opacity-50"
                data-testid="unsnooze-btn"
              >
                Unsnooze
              </button>
            ) : (
              <button
                onClick={() => setSnoozeOpen((v) => !v)}
                className="text-xs font-mono text-slate-400 hover:text-white transition-colors"
                data-testid="snooze-toggle-btn"
              >
                Snooze
              </button>
            )}
            {snoozeOpen && !alertState.snooze_mode && (
              <div className="absolute right-0 top-full mt-1 z-10 w-56 bg-[#0B0F1A] border border-[#1F2937] rounded-md p-3 shadow-xl space-y-2" data-testid="snooze-menu">
                <select
                  value={snoozeReason}
                  onChange={(e) => setSnoozeReason(e.target.value)}
                  className="w-full bg-[#030712] border border-[#1F2937] text-slate-200 text-xs rounded px-2 py-1.5 font-mono"
                  data-testid="snooze-reason-select"
                >
                  {SNOOZE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <button
                  onClick={() => snooze("timed")}
                  disabled={snoozeBusy}
                  className="w-full text-left text-xs font-mono text-slate-200 hover:text-white bg-[#1F2937] hover:bg-[#1F2937]/70 rounded px-2 py-1.5 disabled:opacity-50"
                  data-testid="snooze-7d-btn"
                >
                  7 days
                </button>
                <button
                  onClick={() => snooze("until_worse")}
                  disabled={snoozeBusy}
                  className="w-full text-left text-xs font-mono text-slate-200 hover:text-white bg-[#1F2937] hover:bg-[#1F2937]/70 rounded px-2 py-1.5 disabled:opacity-50"
                  data-testid="snooze-until-worse-btn"
                >
                  Until it gets worse
                </button>
              </div>
            )}
          </div>
        )}
        {canCompute && (
          <button
            onClick={computeSnapshot}
            disabled={computing}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono font-semibold bg-[#1F2937] hover:bg-[#1F2937]/70 text-slate-300 disabled:opacity-50 transition-colors"
            data-testid="refresh-forecast-snapshot-btn"
          >
            {computing
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Computing…</>
              : <><RefreshCw className="w-3.5 h-3.5" /> Refresh</>}
          </button>
        )}
      </div>

      <div className="bg-[#111827] p-5 space-y-4">
        {/* Margin bar + headline beneath it */}
        <div>
          <ForecastMarginBar projected={marginPct} target={targetPct} />
          <p className="mt-3 text-sm text-slate-200" data-testid="forecast-headline">{snapshot.headline}</p>
        </div>

        {/* Why? expander */}
        <button
          onClick={() => setWhyOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-mono text-[#00F5D4] hover:text-white transition-colors"
          data-testid="why-expander-toggle"
        >
          {whyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Why?
        </button>

        {whyOpen && (
          <div className="space-y-4 pt-1" data-testid="why-expander-content">
            {/* Driver table */}
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono font-bold mb-1.5">Drivers</div>
              <div className="border border-[#1F2937] rounded divide-y divide-[#1F2937]">
                {(snapshot.drivers || []).map((d) => (
                  <div key={d.key} className="flex justify-between items-center px-3 py-2 text-xs font-mono">
                    <span className="text-slate-500 uppercase tracking-[0.1em] text-[10px]">{DRIVER_LABELS[d.key] || d.key}</span>
                    <span className="text-slate-200">{fmtDriverValue(d)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 28-day EAC sparkline */}
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono font-bold mb-1.5">
                EAC · trailing 28 days
              </div>
              {sparkline.length >= 2 ? (
                <div className="h-32 bg-[#030712] border border-[#1F2937] rounded p-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparkline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={{ stroke: "#1F2937" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} axisLine={{ stroke: "#1F2937" }} tickLine={false} width={64}
                        tickFormatter={(v) => money(v, snapshot.currency)} />
                      <Tooltip
                        contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 6, fontSize: 11 }}
                        labelStyle={{ color: "#9CA3AF" }}
                        formatter={(v) => [money(v, snapshot.currency), "EAC"]}
                      />
                      <Line type="monotone" dataKey="eac" stroke="#00F5D4" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-xs text-slate-500 font-mono">Not enough snapshots yet for a trend line.</div>
              )}
            </div>

            {/* Attribution split */}
            <div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono font-bold mb-1.5">
                Change attribution {snapshot.top_driver && <span className="normal-case text-slate-600">· top driver: {ATTRIBUTION_LABELS[snapshot.top_driver]}</span>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(snapshot.attribution || {}).map(([key, value]) => (
                  <div
                    key={key}
                    className={`border rounded px-3 py-2 ${key === snapshot.top_driver ? "border-[#00F5D4]/40 bg-[#00F5D4]/5" : "border-[#1F2937] bg-[#030712]"}`}
                    data-testid={`attribution-${key}`}
                  >
                    <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500 font-mono">{ATTRIBUTION_LABELS[key] || key}</div>
                    <div className={`text-sm font-mono mt-0.5 ${value > 0 ? "text-[#FF007A]" : value < 0 ? "text-[#00C896]" : "text-slate-400"}`}>
                      {value > 0 ? "+" : ""}{money(value, snapshot.currency)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Data as of footer — freshness is part of trust */}
        <div className="pt-3 border-t border-[#1F2937] flex flex-wrap gap-x-5 gap-y-1 text-[10px] font-mono text-slate-500" data-testid="forecast-data-as-of">
          <span>Snapshot: {fmtDate(snapshot.as_of)}</span>
          <span>Time logs as of: {fmtDate(snapshot.sources_as_of?.time_logs)}</span>
        </div>
      </div>
    </div>
  );
};

export default ForecastExplainPanel;
